// Un seul client. Le jeton, la collectivité choisie et l'environnement
// voyagent dans les en-têtes : le serveur revérifie les droits à chaque appel.
const state = {
  token: localStorage.getItem('ucivis.token') || '',
  org: localStorage.getItem('ucivis.org') || '',
  env: localStorage.getItem('ucivis.env') || 'draft',
}

export const session = {
  get token() { return state.token },
  get org() { return state.org },
  get env() { return state.env },
  setToken(t) { state.token = t; localStorage.setItem('ucivis.token', t) },
  setOrg(id) { state.org = id; localStorage.setItem('ucivis.org', id) },
  setEnv(e) { state.env = e; localStorage.setItem('ucivis.env', e) },
  clear() { localStorage.clear(); state.token = ''; state.org = ''; },
}

// L'envoi d'image passe par un formulaire multipart : pas de JSON ici.
export async function upload(fichier) {
  const corps = new FormData()
  corps.append('fichier', fichier)
  const res = await fetch('/api/admin/media', {
    method: 'POST',
    headers: {
      ...(state.token ? { Authorization: `Bearer ${state.token}` } : {}),
      ...(state.org ? { 'X-Ucivis-Org': state.org } : {}),
      'X-Ucivis-Env': state.env,
    },
    body: corps,
  })
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    throw new Error(typeof d.detail === 'string' ? d.detail : "L'image n'a pas pu être envoyée")
  }
  return res.json()
}

async function req(path, { method = 'GET', body } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(state.token ? { Authorization: `Bearer ${state.token}` } : {}),
      ...(state.org ? { 'X-Ucivis-Org': state.org } : {}),
      'X-Ucivis-Env': state.env,
    },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (res.status === 401) { session.clear(); window.location.reload() }
  if (!res.ok) {
    const d = await res.json().catch(() => ({}))
    if (d.detail && typeof d.detail === 'object') {
      // refus motivé : le front peut proposer la suite plutôt qu'un message sec
      const erreur = new Error(d.detail.message || 'Erreur inattendue')
      erreur.detail = d.detail
      throw erreur
    }
    throw new Error(typeof d.detail === 'string' ? d.detail : 'Erreur inattendue')
  }
  return res.status === 204 ? null : res.json()
}

export const api = {
  config: () => req('/api/console/config'),
  login: (email, password) => req('/api/auth/login', { method: 'POST', body: { email, password } }),
  me: () => req('/api/auth/me'),
  orgs: () => req('/api/console/orgs'),
  createOrg: (payload) => req('/api/console/orgs', { method: 'POST', body: payload }),
  shared: (orgId) => req(`/api/console/orgs/${orgId}/shared`),
  adopt: (orgId, key) => req(`/api/console/orgs/${orgId}/adopt/${key}`, { method: 'POST' }),

  requests: (status = 'open', q = '') => req(`/api/admin/requests?status=${status}&q=${encodeURIComponent(q)}`),
  batchActions: (ids) => req('/api/admin/requests/batch-actions', { method: 'POST', body: { ids } }),
  batch: (ids, action_id, comment = '') =>
    req('/api/admin/requests/batch', { method: 'POST', body: { ids, action_id, comment } }),
  voisins: (id, status = 'open', q = '') =>
    req(`/api/admin/requests/${id}/voisins?status=${status}&q=${encodeURIComponent(q)}`),
  creerDemarche: (payload) => req('/api/admin/forms', { method: 'POST', body: payload }),
  request: (id) => req(`/api/admin/requests/${id}`),
  action: (id, actionId, comment = '') =>
    req(`/api/admin/requests/${id}/actions/${actionId}`, { method: 'POST', body: { comment } }),
  note: (id, text) => req(`/api/admin/requests/${id}/note`, { method: 'POST', body: { text } }),

  catalog: () => req('/api/admin/catalog'),
  install: (key) => req(`/api/admin/catalog/${key}/install`, { method: 'POST' }),
  forms: () => req('/api/admin/forms'),
  fieldCatalog: () => req('/api/admin/field-catalog'),
  rubriques: () => req('/api/admin/rubriques'),
  formats: (type) => req(`/api/admin/formats${type ? `?type=${type}` : ''}`),
  formUsage: (key) => req(`/api/admin/forms/${key}/usage`),
  form: (key) => req(`/api/admin/forms/${key}`),
  saveForm: (key, payload) => req(`/api/admin/forms/${key}`, { method: 'PATCH', body: payload }),
  removeForm: (key) => req(`/api/admin/forms/${key}`, { method: 'DELETE' }),

  workflows: () => req('/api/admin/workflows'),
  workflow: (key) => req(`/api/admin/workflows/${key}`),
  saveWorkflow: (key, payload) => req(`/api/admin/workflows/${key}`, { method: 'PATCH', body: payload }),
  vocabulary: (key) => req(`/api/admin/workflows/${key}/vocabulary`),
  simulate: (key, payload) => req(`/api/admin/workflows/${key}/simulate`, { method: 'POST', body: payload }),

  dashboard: () => req('/api/admin/dashboard'),
  miseEnRoute: () => req('/api/admin/mise-en-route'),
  bilan: (mois = 12) => req(`/api/admin/bilan?mois=${mois}`),
  formsStatus: () => req('/api/admin/forms-status'),
  suspend: (key, reason) => req(`/api/admin/forms/${key}/suspend`, { method: 'POST', body: { reason } }),
  resume: (key) => req(`/api/admin/forms/${key}/resume`, { method: 'POST' }),

  mails: () => req('/api/admin/mails'),
  saveMail: (key, payload) => req(`/api/admin/mails/${key}`, { method: 'PATCH', body: payload }),
  createMail: (payload) => req('/api/admin/mails', { method: 'POST', body: payload }),
  deleteMail: (key) => req(`/api/admin/mails/${key}`, { method: 'DELETE' }),
  mailPreview: (key) => req(`/api/admin/mails/${key}/preview`),
  mailVariables: () => req('/api/admin/mail-variables'),

  agendas: () => req('/api/admin/agendas'),
  saveAgenda: (key, payload) => req(`/api/admin/agendas/${key}`, { method: 'PATCH', body: payload }),
  createAgenda: () => req('/api/admin/agendas', { method: 'POST' }),
  bookings: () => req('/api/admin/bookings'),
  cancelBooking: (id) => req(`/api/admin/bookings/${id}`, { method: 'DELETE' }),

  outbox: () => req('/api/admin/outbox'),
  workflowTemplates: () => req('/api/admin/workflow-templates'),
  actionCatalog: () => req('/api/admin/action-catalog'),
  deleteWorkflow: (key) => req(`/api/admin/workflows/${key}`, { method: 'DELETE' }),
  createWorkflow: (payload) => req('/api/admin/workflows', { method: 'POST', body: payload }),
  org: () => req('/api/admin/org'),
  profile: () => req('/api/admin/profile'),
  saveProfile: (payload) => req('/api/admin/profile', { method: 'PATCH', body: payload }),
  changePassword: (payload) => req('/api/admin/profile/password', { method: 'POST', body: payload }),
  roles: () => req('/api/admin/roles'),
  donneesApercu: () => req('/api/admin/donnees/apercu'),
  reglerRetention: (key, payload) =>
    req(`/api/admin/donnees/demarches/${key}`, { method: 'PATCH', body: payload }),
  marquerChamp: (key, varname, personnel, justification = '') =>
    req(`/api/admin/donnees/demarches/${key}/champ`,
        { method: 'PATCH', body: { varname, personnel, justification } }),
  registre: () => req('/api/admin/donnees/registre'),
  echeances: () => req('/api/admin/donnees/echeances'),
  gelerDossier: (id, gele, motif) =>
    req(`/api/admin/donnees/dossiers/${id}/gel`, { method: 'POST', body: { gele, motif } }),
  effacerDossier: (id, motif) =>
    req(`/api/admin/donnees/dossiers/${id}/effacer`, { method: 'POST', body: { motif } }),
  services: () => req('/api/admin/services'),
  createService: (payload) => req('/api/admin/services', { method: 'POST', body: payload }),
  saveService: (key, payload) => req(`/api/admin/services/${key}`, { method: 'PATCH', body: payload }),
  deleteService: (key) => req(`/api/admin/services/${key}`, { method: 'DELETE' }),
  serviceAgents: (key) => req(`/api/admin/services/${key}/agents`),
  setServiceAgent: (key, user_id, dedans) =>
    req(`/api/admin/services/${key}/agents`, { method: 'POST', body: { user_id, dedans } }),
  formAcces: (key) => req(`/api/admin/forms/${key}/acces`),
  reglerAcces: (key, payload) =>
    req(`/api/admin/forms/${key}/acces`, { method: 'POST', body: payload }),
  handoverOptions: (id) => req(`/api/admin/requests/${id}/handover`),
  handover: (id, payload) => req(`/api/admin/requests/${id}/handover`, { method: 'POST', body: payload }),
  agents: () => req('/api/admin/agents'),
  agentScope: (id) => req(`/api/admin/agents/${id}/scope`),
  setAgentScope: (id, payload) =>
    req(`/api/admin/agents/${id}/scope`, { method: 'POST', body: payload }),
  addAgent: (payload) => req('/api/admin/agents', { method: 'POST', body: payload }),
  setAgentRole: (id, role) => req(`/api/admin/agents/${id}`, { method: 'PATCH', body: { role } }),
  removeAgent: (id) => req(`/api/admin/agents/${id}`, { method: 'DELETE' }),
  resetAgentPassword: (id) => req(`/api/admin/agents/${id}/reset-password`, { method: 'POST' }),
  saveOrg: (payload) => req('/api/admin/org', { method: 'PATCH', body: payload }),

  blockCatalog: () => req('/api/admin/block-catalog'),
  sectionCatalog: () => req('/api/admin/section-catalog'),
  applyLayout: (key) => req('/api/admin/site/layout', { method: 'POST', body: { key } }),
  media: () => req('/api/admin/media'),
  deleteMedia: (nom) => req(`/api/admin/media/${nom}`, { method: 'DELETE' }),
  site: () => req('/api/admin/site'),
  saveSite: (payload) => req('/api/admin/site', { method: 'PATCH', body: payload }),
  publishPreview: () => req('/api/admin/publish'),
  publish: (resume = []) => req('/api/admin/publish', { method: 'POST', body: { resume } }),
}
